import { SchemaField } from '../services/TemplatesService';

export interface SchemaTemplate {
  required_fields: SchemaField[];
  optional_fields: SchemaField[];
}

export const MINIJOB_SCHEMA_V2: SchemaTemplate = {
  required_fields: [
    {
      key: 'start_date',
      label: 'Beginn des Arbeitsverhältnisses',
      type: 'date',
      editable_in: 'wizard',
      profile_map: 'employee.start_date',
      step_hint: 'basic_info',
    },
    {
      key: 'probation_notice_weeks',
      label: 'Kündigungsfrist in der Probezeit (Wochen)',
      type: 'number',
      default_value: 2,
      editable_in: 'wizard',
      step_hint: 'clauses',
    },
    {
      key: 'hourly_rate',
      label: 'Stundenlohn (EUR)',
      type: 'decimal',
      default_value: 12.82,
      editable_in: 'wizard',
      step_hint: 'compensation',
    },
    {
      key: 'monthly_hours',
      label: 'Monatliche Stunden (Minijob)',
      type: 'decimal',
      editable_in: 'wizard',
      help_text: 'Wird bei Minijob aus 556 € / Stundenlohn berechnet; kann manuell angepasst werden.',
      step_hint: 'compensation',
    },
  ],
  optional_fields: [
    /* ---------- TENANT (aus Einstellungen, nicht im Wizard) ---------- */
    {
      key: 'tenant.legal_name',
      label: 'Firma (rechtlich)',
      type: 'string',
      editable_in: 'template',
    },
    {
      key: 'tenant.signatory_name',
      label: 'Unterzeichner:in',
      type: 'string',
      editable_in: 'template',
    },
    {
      key: 'tenant.street',
      label: 'Straße',
      type: 'string',
      editable_in: 'template',
    },
    {
      key: 'tenant.house_no',
      label: 'Hausnummer',
      type: 'string',
      editable_in: 'template',
    },
    {
      key: 'tenant.postal_code',
      label: 'PLZ',
      type: 'string',
      editable_in: 'template',
    },
    {
      key: 'tenant.city',
      label: 'Ort',
      type: 'string',
      editable_in: 'template',
    },

    /* ---------- EMPLOYEE (aus Stammdaten, nicht im Wizard) ---------- */
    {
      key: 'employee.gender',
      label: 'Geschlecht (M/F/w/m)',
      type: 'string',
      editable_in: 'template',
      profile_map: 'employee.gender',
    },
    {
      key: 'employee.first_name',
      label: 'Vorname',
      type: 'string',
      editable_in: 'template',
      profile_map: 'employee.first_name',
    },
    {
      key: 'employee.last_name',
      label: 'Nachname',
      type: 'string',
      editable_in: 'template',
      profile_map: 'employee.last_name',
    },
    {
      key: 'employee.street',
      label: 'Straße',
      type: 'string',
      editable_in: 'template',
      profile_map: 'employee.street',
    },
    {
      key: 'employee.house_no',
      label: 'Hausnummer',
      type: 'string',
      editable_in: 'template',
      profile_map: 'employee.house_no',
    },
    {
      key: 'employee.zip',
      label: 'PLZ',
      type: 'string',
      editable_in: 'template',
      profile_map: 'employee.zip',
    },
    {
      key: 'employee.city',
      label: 'Ort',
      type: 'string',
      editable_in: 'template',
      profile_map: 'employee.city',
    },
    {
      key: 'employee.position_title',
      label: 'Position / Funktion',
      type: 'string',
      editable_in: 'wizard',
      step_hint: 'basic_info',
      default_value: 'Mitarbeiter',
    },

    /* ---------- WEITERE WIZARD-FELDER ---------- */
    {
      key: 'work_location_city',
      label: 'Arbeitsort (Stadt)',
      type: 'string',
      default_value: 'Berlin',
      editable_in: 'wizard',
      step_hint: 'basic_info',
    },

    /* §7 Weitere Beschäftigungen (Conditional) */
    {
      key: 'has_other_employment',
      label: 'Weitere Beschäftigung vorhanden?',
      type: 'string',
      default_value: 'false',
      editable_in: 'wizard',
      step_hint: 'clauses',
      help_text: 'Erlaubte Werte: true/false (robust geparst)',
    },
    {
      key: 'other_employment_employer',
      label: 'Arbeitgeber (weitere Besch.)',
      type: 'string',
      editable_in: 'wizard',
      step_hint: 'clauses',
    },
    {
      key: 'other_employment_start_date',
      label: 'Beginn (weitere Besch.)',
      type: 'date',
      editable_in: 'wizard',
      step_hint: 'clauses',
    },
    {
      key: 'other_employment_monthly_salary',
      label: 'Entgelt / Monat (weitere Besch.)',
      type: 'decimal',
      editable_in: 'wizard',
      step_hint: 'clauses',
    },

    /* Urlaub */
    {
      key: 'holiday_min_days',
      label: 'Gesetzlicher Mindesturlaub (Tage)',
      type: 'number',
      editable_in: 'template',
      default_value: 20,
    },
    {
      key: 'holiday_extra_days_per_year',
      label: 'Zusätzliche Urlaubstage (Tage)',
      type: 'number',
      editable_in: 'wizard',
      step_hint: 'clauses',
      default_value: 0,
    },
    {
      key: 'total_leave_days_per_year',
      label: 'Gesamturlaub/Jahr (Tage)',
      type: 'number',
      editable_in: 'wizard',
      step_hint: 'clauses',
      help_text: '= gesetzlich + zusätzlich',
    },

    /* Vertragsstrafe & Kündigungsfrist */
    {
      key: 'penalty_amount_eur',
      label: 'Vertragsstrafe No‑Show (EUR)',
      type: 'decimal',
      editable_in: 'wizard',
      step_hint: 'clauses',
      default_value: 150.00,
    },
    {
      key: 'notice_after_probation_weeks',
      label: 'Kündigungsfrist nach Probezeit (Wo.)',
      type: 'number',
      editable_in: 'wizard',
      step_hint: 'clauses',
      default_value: 4,
    },

    /* Dokument‑Metadaten */
    {
      key: 'document_place',
      label: 'Ort (Unterschrift)',
      type: 'string',
      editable_in: 'wizard',
      step_hint: 'clauses',
      default_value: 'Berlin',
      profile_map: 'employee.city',
    },
    {
      key: 'document_date',
      label: 'Datum (Unterschrift)',
      type: 'date',
      editable_in: 'wizard',
      step_hint: 'clauses',
    },

    /* Freitext‑Beschreibung */
    {
      key: 'role_description_text',
      label: 'Tätigkeitsbeschreibung (Text)',
      type: 'text',
      editable_in: 'wizard',
      step_hint: 'clauses',
      default_value: 'Herstellung von Speiseeis und ähnlichen Produkten; weitere zumutbare Aufgaben ohne Lohnminderung.',
    },

    /* Minijob‑Grenze (nur Template‑Level) */
    {
      key: 'minijob_target_earnings_eur',
      label: 'Minijob‑Grenze (EUR/Monat)',
      type: 'decimal',
      editable_in: 'template',
      step_hint: 'compensation',
      default_value: 556.00,
    },
  ],
};

/*
 * ============================================
 * FALLBACK - ALTES SCHEMA (falls neues nicht funktioniert)
 * ============================================
 *
 * Falls das neue Schema Probleme macht, kannst du zurück zu diesem Schema wechseln:
 *
 * export const MINIJOB_SCHEMA_V2_OLD: SchemaTemplate = {
 *   required_fields: [
 *     {
 *       key: 'start_date',
 *       label: 'Beginn des Arbeitsverhältnisses',
 *       type: 'date',
 *       editable_in: 'wizard',
 *       profile_map: 'employee.start_date',
 *       step_hint: 'basic_info',
 *       default_value: '',
 *     },
 *     {
 *       key: 'employee.position_title',
 *       label: 'Position / Funktion',
 *       type: 'string',
 *       editable_in: 'wizard',
 *       profile_map: 'employee.position_title',
 *       step_hint: 'basic_info',
 *       default_value: 'Produktionskraft',
 *     },
 *     {
 *       key: 'hourly_rate',
 *       label: 'Stundenlohn (EUR)',
 *       type: 'decimal',
 *       editable_in: 'wizard',
 *       step_hint: 'compensation',
 *       default_value: '12.82',
 *       help_text: 'Mindestens gesetzlicher Mindestlohn (2025: 12,82 €).',
 *     },
 *     {
 *       key: 'monthly_hours',
 *       label: 'Monatliche Stunden (Minijob)',
 *       type: 'decimal',
 *       editable_in: 'wizard',
 *       step_hint: 'compensation',
 *       default_value: '',
 *       help_text: 'Wird automatisch aus 556 € / Stundenlohn vorgeschlagen; bei Bedarf anpassen.',
 *     },
 *     {
 *       key: 'has_other_employment', 
 *       label: 'Hat weitere Beschäftigung?',
 *       type: 'select',
 *       editable_in: 'wizard',
 *       step_hint: 'basic_info',
 *       default_value: 'false',
 *       options: [
 *         { value: 'false', label: 'Nein' },
 *         { value: 'true', label: 'Ja' },
 *       ],
 *       help_text: 'Ist der Arbeitnehmer bei einen weiteren Arbeitgeber beschäftigt?',
 *     },
 *   ],
 *   optional_fields: [... wie vorher ...]
 * };
 */
